// src/components/ecommerce/CartSidebar.jsx
import React, { useState, useRef, useEffect } from 'react';
import { 
  X, 
  ShoppingCart,
  Plus,
  Minus,
  Trash2,
  MessageCircle,
  Mail,
  Phone,
  Package,
  Truck,
  CreditCard,
  Shield,
  Tag,
  ChevronDown,
  ArrowRight,
  MapPin,
  Gift
} from 'lucide-react';
import { useCart } from '../../contexts/CartContext';
import { CheckoutModal } from './CheckoutModal';

export const CartSidebar = ({ 
  isOpen, 
  onClose, 
  onContact,
  storeSettings = {},
  styling = {},
  className = '' 
}) => {
  const { 
    cart, 
    updateQuantity, 
    removeFromCart, 
    clearCart,
    getCartSubtotal,
    getCartTax,
    getCartShipping,
    getCartGrandTotal,
    getCartItemCount
  } = useCart();

  const [promoCode, setPromoCode] = useState('');
  const [appliedPromo, setAppliedPromo] = useState(null);
  const [selectedDeliveryZone, setSelectedDeliveryZone] = useState(null);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showContactButtons, setShowContactButtons] = useState(false);
  
  const cartItemsRef = useRef(null);
  const contactButtonsRef = useRef(null);

  // Initialize default delivery zone
  useEffect(() => {
    if (storeSettings.shipping?.deliveryZones?.length > 0 && !selectedDeliveryZone) {
      setSelectedDeliveryZone(storeSettings.shipping.deliveryZones[0]);
    }
  }, [storeSettings.shipping?.deliveryZones, selectedDeliveryZone]);

  // Auto-scroll to contact buttons when cart is updated
  useEffect(() => {
    if (cart.length > 0 && contactButtonsRef.current) {
      const timer = setTimeout(() => {
        setShowContactButtons(true);
      }, 500);
      return () => clearTimeout(timer);
    }
  }, [cart.length]);

  const handleQuantityChange = (item, newQuantity) => {
    if (newQuantity <= 0) {
      removeFromCart(item.id, item.variant);
    } else {
      updateQuantity(item.id, newQuantity, item.variant);
    }
  };

  const handlePromoCode = () => {
    if (promoCode.toLowerCase() === 'welcome10') {
      setAppliedPromo({
        code: 'WELCOME10',
        discount: 0.1,
        type: 'percentage'
      });
    } else if (promoCode.toLowerCase() === 'save50') {
      setAppliedPromo({
        code: 'SAVE50',
        discount: 50,
        type: 'fixed'
      });
    } else {
      setAppliedPromo(null);
    }
    setPromoCode('');
  };

  const handleContactOrder = (type) => {
    const cartSummary = cart.map(item => 
      `${item.name} (Qty: ${item.quantity}) - R${(item.price * item.quantity).toFixed(2)}`
    ).join('\n');
    
    const total = calculateGrandTotal();
    
    const message = `Hi! I'd like to place an order:\n\n${cartSummary}\n\nTotal: R${total.toFixed(2)}\n\nPlease confirm availability and payment details.`;
    
    onContact?.(type, type === 'whatsapp' ? storeSettings.whatsappNumber : storeSettings.email, message);
  };

  const scrollToContactButtons = () => {
    if (contactButtonsRef.current) {
      contactButtonsRef.current.scrollIntoView({ 
        behavior: 'smooth', 
        block: 'end' 
      });
      setShowContactButtons(true);
    }
  };

  const calculateSubtotal = () => {
    return cart.reduce((total, item) => total + (item.price * item.quantity), 0);
  };

  const calculateShipping = () => {
    if (!selectedDeliveryZone || !storeSettings.shipping?.enabled) return 0;
    
    const subtotal = calculateSubtotal();
    
    // Check for free shipping
    if (storeSettings.shipping?.freeShippingEnabled && 
        storeSettings.shipping?.freeShippingThreshold && 
        subtotal >= storeSettings.shipping.freeShippingThreshold) {
      return 0;
    }
    
    return selectedDeliveryZone.fee || 0;
  };

  const calculateTax = () => {
    const subtotal = calculateSubtotal();
    const taxRate = (storeSettings.tax?.rate || 15) / 100;
    return storeSettings.tax?.enabled ? subtotal * taxRate : 0;
  };

  const calculateDiscount = () => {
    if (!appliedPromo) return 0;
    
    const subtotal = calculateSubtotal();
    if (appliedPromo.type === 'percentage') {
      return subtotal * appliedPromo.discount;
    } else {
      return Math.min(appliedPromo.discount, subtotal);
    }
  };

  const calculateGrandTotal = () => {
    const subtotal = calculateSubtotal();
    const shipping = calculateShipping();
    const tax = calculateTax();
    const discount = calculateDiscount();
    
    return Math.max(0, subtotal + shipping + tax - discount);
  };

  const subtotal = calculateSubtotal();
  const shipping = calculateShipping();
  const tax = calculateTax();
  const discount = calculateDiscount();
  const grandTotal = calculateGrandTotal();
  const isEligibleForFreeShipping = storeSettings.shipping?.freeShippingEnabled && 
    storeSettings.shipping?.freeShippingThreshold &&
    subtotal >= storeSettings.shipping.freeShippingThreshold;

  return (
    <>
      {/* Backdrop */}
      <div 
        className={`fixed inset-0 bg-black bg-opacity-50 transition-opacity z-40 ${
          isOpen ? 'opacity-100 visible' : 'opacity-0 invisible'
        }`}
        onClick={onClose}
      />

      {/* Sidebar */}
      <div className={`
        fixed top-0 right-0 h-full w-full max-w-md bg-white shadow-2xl transform transition-transform duration-300 ease-in-out z-50
        ${isOpen ? 'translate-x-0' : 'translate-x-full'}
        ${className}
      `}>
        {/* Use CSS Grid for proper layout */}
        <div className="h-full grid grid-rows-[auto_1fr_auto]">
          
          {/* Header - Fixed at top */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 bg-white">
            <div className="flex items-center gap-2">
              <ShoppingCart className="h-5 w-5 text-gray-700" />
              <h2 className="text-lg font-semibold text-gray-900">
                Shopping Cart ({getCartItemCount()})
              </h2>
            </div>
            <button
              onClick={onClose}
              className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
            >
              <X className="h-5 w-5" />
            </button>
          </div>

          {/* Main Content Area - Scrollable */}
          <div className="overflow-y-auto min-h-0">
            {cart.length === 0 ? (
              /* Empty Cart State */
              <div className="flex flex-col items-center justify-center h-full p-8 text-center">
                <div className="w-20 h-20 bg-gray-100 rounded-full flex items-center justify-center mb-4">
                  <ShoppingCart className="h-10 w-10 text-gray-400" />
                </div>
                <h3 className="text-lg font-medium text-gray-900 mb-2">Your cart is empty</h3>
                <p className="text-gray-500 mb-6">Add some items to get started!</p>
                <button
                  onClick={onClose}
                  className="bg-blue-600 text-white px-6 py-2 rounded-lg hover:bg-blue-700 transition-colors"
                >
                  Continue Shopping
                </button>
              </div>
            ) : (
              <div className="p-4 space-y-4">
                {/* Cart Items */}
                {cart.map((item) => (
                  <div key={`${item.id}-${item.variant || 'default'}`} 
                       className="bg-gray-50 rounded-lg p-4">
                    <div className="flex gap-3">
                      {/* Item Image */}
                      <div className="w-16 h-16 bg-gray-200 rounded-lg overflow-hidden flex-shrink-0">
                        {item.imageUrl ? (
                          <img 
                            src={item.imageUrl} 
                            alt={item.name}
                            className="w-full h-full object-cover"
                          />
                        ) : (
                          <div className="w-full h-full flex items-center justify-center">
                            <Package className="h-6 w-6 text-gray-400" />
                          </div>
                        )}
                      </div>

                      {/* Item Details */}
                      <div className="flex-1 min-w-0">
                        <h4 className="font-medium text-gray-900 truncate">{item.name}</h4>
                        {item.variant && (
                          <p className="text-sm text-gray-500">{item.variant}</p>
                        )}
                        <div className="flex items-center justify-between mt-2">
                          <span className="font-medium text-gray-900">
                            R{(item.price * item.quantity).toFixed(2)}
                          </span>
                          <div className="flex items-center gap-2">
                            <button
                              onClick={() => handleQuantityChange(item, item.quantity - 1)}
                              className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                            >
                              <Minus className="h-4 w-4" />
                            </button>
                            <span className="w-8 text-center font-medium">{item.quantity}</span>
                            <button
                              onClick={() => handleQuantityChange(item, item.quantity + 1)}
                              className="w-8 h-8 rounded-full bg-white border border-gray-300 flex items-center justify-center hover:bg-gray-50 transition-colors"
                            >
                              <Plus className="h-4 w-4" />
                            </button>
                            <button
                              onClick={() => removeFromCart(item.id, item.variant)}
                              className="ml-2 p-1 text-red-500 hover:bg-red-50 rounded transition-colors"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                ))}

                {/* Delivery Zone Selection */}
                {storeSettings.shipping?.enabled && storeSettings.shipping?.deliveryZones?.length > 0 && (
                  <div className="border-t border-gray-200 pt-4">
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      Delivery Area
                    </label>
                    <select
                      value={selectedDeliveryZone?.name || ''}
                      onChange={(e) => {
                        const zone = storeSettings.shipping.deliveryZones.find(z => z.name === e.target.value);
                        setSelectedDeliveryZone(zone);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                    >
                      {storeSettings.shipping.deliveryZones.map((zone) => (
                        <option key={zone.name} value={zone.name}>
                          {zone.name} - R{zone.fee} ({zone.deliveryTime})
                        </option>
                      ))}
                    </select>
                  </div>
                )}

                {/* Promo Code Section */}
                <div className="border-t border-gray-200 pt-4">
                  <div className="flex gap-2">
                    <input
                      type="text"
                      placeholder="Promo code"
                      value={promoCode}
                      onChange={(e) => setPromoCode(e.target.value)}
                      className="flex-1 px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    />
                    <button
                      onClick={handlePromoCode}
                      className="px-4 py-2 bg-gray-100 hover:bg-gray-200 rounded-lg text-sm font-medium transition-colors"
                    >
                      Apply
                    </button>
                  </div>
                  {appliedPromo && (
                    <div className="mt-2 flex items-center gap-1 text-green-600 text-sm">
                      <Tag className="h-4 w-4" />
                      <span>{appliedPromo.code} applied (-R{discount.toFixed(2)})</span>
                    </div>
                  )}
                </div>

                {/* Free Shipping Notification */}
                {storeSettings.shipping?.freeShippingEnabled && storeSettings.shipping?.freeShippingThreshold && (
                  <div className="border-t border-gray-200 pt-4">
                    {isEligibleForFreeShipping ? (
                      <div className="flex items-center gap-2 text-green-600 text-sm">
                        <Gift className="h-4 w-4" />
                        <span>You qualify for free shipping!</span>
                      </div>
                    ) : (
                      <div className="flex items-center gap-2 text-blue-600 text-sm">
                        <Truck className="h-4 w-4" />
                        <span>
                          Add R{(storeSettings.shipping.freeShippingThreshold - subtotal).toFixed(2)} more for free shipping
                        </span>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>

          {/* Footer Actions - Fixed at bottom */}
          {cart.length > 0 && (
            <div className="border-t border-gray-200 bg-white">
              {/* Order Summary */}
              <div className="p-4 space-y-2 text-sm border-b border-gray-100">
                <div className="flex justify-between">
                  <span className="text-gray-600">Subtotal</span>
                  <span>R{subtotal.toFixed(2)}</span>
                </div>
                
                {appliedPromo && (
                  <div className="flex justify-between text-green-600">
                    <span>Discount ({appliedPromo.code})</span>
                    <span>-R{discount.toFixed(2)}</span>
                  </div>
                )}
                
                {storeSettings.shipping?.enabled && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">
                      Delivery {selectedDeliveryZone ? `(${selectedDeliveryZone.name})` : ''}
                    </span>
                    <span>
                      {isEligibleForFreeShipping ? (
                        <span className="text-green-600">FREE</span>
                      ) : (
                        `R${shipping.toFixed(2)}`
                      )}
                    </span>
                  </div>
                )}
                
                {storeSettings.tax?.enabled && (
                  <div className="flex justify-between">
                    <span className="text-gray-600">VAT ({storeSettings.tax.rate}%)</span>
                    <span>R{tax.toFixed(2)}</span>
                  </div>
                )}
                
                <div className="border-t border-gray-200 pt-2 flex justify-between font-semibold text-lg">
                  <span>Total</span>
                  <span>R{grandTotal.toFixed(2)}</span>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="p-4 space-y-3 bg-gray-50">
                {/* Modern Checkout Button */}
                <button
                  onClick={() => setShowCheckout(true)}
                  className="w-full bg-blue-600 text-white py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center justify-center gap-2 font-medium"
                >
                  <CreditCard className="h-5 w-5" />
                  Proceed to Checkout
                  <ArrowRight className="h-4 w-4" />
                </button>

                {/* Quick Contact Options */}
                <div className="text-center">
                  <p className="text-xs text-gray-600 mb-2">Or order directly:</p>
                  <div className="flex gap-2">
                    {storeSettings.whatsappNumber && (
                      <button
                        onClick={() => handleContactOrder('whatsapp')}
                        className="flex-1 bg-green-600 text-white py-2 rounded-lg hover:bg-green-700 transition-colors flex items-center justify-center gap-1 text-sm"
                      >
                        <MessageCircle className="h-4 w-4" />
                        WhatsApp
                      </button>
                    )}

                    {storeSettings.email && (
                      <button
                        onClick={() => handleContactOrder('email')}
                        className="flex-1 bg-gray-600 text-white py-2 rounded-lg hover:bg-gray-700 transition-colors flex items-center justify-center gap-1 text-sm"
                      >
                        <Mail className="h-4 w-4" />
                        Email
                      </button>
                    )}
                  </div>
                </div>

                {/* Trust Elements */}
                <div className="flex items-center justify-center gap-4 pt-2 text-xs text-gray-500">
                  <div className="flex items-center gap-1">
                    <Shield className="h-3 w-3" />
                    Secure
                  </div>
                  <div className="flex items-center gap-1">
                    <Truck className="h-3 w-3" />
                    Fast Delivery
                  </div>
                  <div className="flex items-center gap-1">
                    <CreditCard className="h-3 w-3" />
                    Easy Payment
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Checkout Modal */}
      {showCheckout && (
        <CheckoutModal
          isOpen={showCheckout}
          onClose={() => setShowCheckout(false)}
          cart={cart}
          storeSettings={storeSettings}
          orderSummary={{
            subtotal,
            shipping,
            tax,
            discount,
            grandTotal,
            selectedDeliveryZone,
            appliedPromo
          }}
          onOrderComplete={() => {
            clearCart();
            setShowCheckout(false);
            onClose();
          }}
        />
      )}
    </>
  );
};